import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { NbDialogService } from '@nebular/theme';
import { TaskStatus } from '../../models/response/task-status';
import { TaskType } from '../../models/response/task-type';
import { City } from '../../models/response/address/city';
import { Buyer } from '../../models/response/buyer/buyer';
import { BuyerCreateComponent } from '../buyer-create/buyer-create.component';
import { Company } from '../../models/response/prospect/company';
import { BuyerRequest } from '../../models/request/buyer/buyer-request';
import { BuyerService } from '../../services/buyer.service';
import { CommonService } from '../../services/common.service';
import { BuyerAgentNote } from '../../models/response/buyer/buyer-agent-note';
import { CompanyUtils } from '../../common/company-utils';
import { Address } from '../../models/response/address/address';
import { BuyerLookupComponent } from '../buyer-create/buyer-lookup/buyer-lookup.component';
import { ProspectLivingAreaLookupRequest } from '../../models/request/prospect/prospect-living-area-lookup-request';

type TenantSection = 'T' | 'A' | 'P' | 'L';

@Component({
  selector: 'ngx-tenant-buyer-create-v2',
  templateUrl: './tenant-buyer-create-v2.component.html',
  styleUrls: ['./tenant-buyer-create-v2.component.scss'],
})
export class TenantBuyerCreateV2Component {
  @ViewChild(BuyerCreateComponent) buyerCreate: BuyerCreateComponent;

  activeSection: TenantSection = 'T';
  isBlank = true;
  isCreateMode = false;
  isSectionView = false;
  isBulkUpdateMode = false;
  isSavingAgentNote = false;
  bulkBuyerIds: number[] = [];
  newAgentNote = '';
  companyBrands: string[] = [];
  currentRentalBrand: string;
  previousRentalBrand: string;
  currentRentalCompanies: Company[] = [];
  previousRentalCompanies: Company[] = [];
  private companyOptions: Company[] = [];

  @Input() cities: City[];
  @Input() taskTypes: TaskType[];
  @Input() taskStatuses: TaskStatus[];
  @Input()
  set companies(value: Company[]) {
    this.companyOptions = value || [];
    this.companyBrands = CompanyUtils.brands(this.companyOptions);
    this.currentRentalCompanies = CompanyUtils.filterByBrand(this.companyOptions, this.currentRentalBrand);
    this.previousRentalCompanies = CompanyUtils.filterByBrand(this.companyOptions, this.previousRentalBrand);
  }

  @Output() editDone = new EventEmitter();
  @Output() newDone = new EventEmitter();
  @Output() reserveBuyer = new EventEmitter();
  @Output() ownSet = new EventEmitter();
  @Output() navigateBuyerEvent = new EventEmitter();
  @Output() backToList = new EventEmitter();

  constructor(private buyerService: BuyerService,
              private commonService: CommonService,
              private dialogService: NbDialogService) {
  }

  setActiveSection(section: TenantSection) {
    if (this.isSectionView && this.activeSection === section && !this.isCreateMode) {
      this.isSectionView = false;
      return;
    }
    this.activeSection = section;
    if (!this.isBlank && !this.isCreateMode) {
      this.isSectionView = true;
    }
  }

  setBuyer(buyer: Buyer) {
    this.isBulkUpdateMode = false;
    this.newAgentNote = '';
    this.bulkBuyerIds = [];
    this.activeSection = 'T';
    this.isBlank = !buyer;
    this.isCreateMode = false;
    this.isSectionView = false;
    if (buyer) {
      this.buyerCreate?.setBuyer(buyer);
    } else {
      this.buyerCreate?.clear();
    }
    this.setRentalCompanyNames(buyer);
  }

  setBuyers(buyers: Buyer[]) {
    this.buyerCreate?.setBuyers(buyers);
  }

  new() {
    this.isBulkUpdateMode = false;
    this.newAgentNote = '';
    this.bulkBuyerIds = [];
    this.activeSection = 'T';
    this.isBlank = false;
    this.isCreateMode = true;
    this.isSectionView = false;
    this.buyerCreate?.setBuyer(null);
    this.buyerCreate?.new();
    this.forceTenantMode();
    this.setRentalCompanyNames(null);
  }

  startBulkUpdate(buyerIds: number[], mode: 'B' | 'T') {
    this.activeSection = 'T';
    this.isBlank = false;
    this.isCreateMode = false;
    this.isSectionView = false;
    this.isBulkUpdateMode = true;
    this.newAgentNote = '';
    this.bulkBuyerIds = buyerIds || [];
    this.buyerCreate?.startBulkUpdate(mode);
    this.forceTenantMode();
    Object.keys(this.buyerCreate?.buyerForm?.controls || {}).forEach(key => this.buyerCreate.buyerForm.get(key).markAsPristine());
    this.setRentalCompanyNames(null);
  }

  back() {
    this.backToList.emit();
  }

  save() {
    if (this.buyerCreate?.buyerForm?.get('buyerCode')?.value === 'T') {
      this.forceTenantMode();
    }
    if (this.isBulkUpdateMode) {
      this.buyerCreate?.submitBulkUpdate(this.bulkBuyerIds, result => this.editDone.emit(result));
      return;
    }
    this.buyerCreate?.submit();
  }

  createTask() {
    this.buyerCreate?.createTask();
  }

  navigateBuyer(forward: boolean) {
    this.buyerCreate?.navigate(forward);
  }

  setBuyerMode(mode: 'buyer' | 'tenant') {
    const form = this.buyerCreate?.buyerForm;
    if (!form) {
      return;
    }
    const buyerCode = mode === 'tenant' ? 'T' : 'B';
    const buyerId = this.buyerCreate?.data?.buyerId;
    if (this.isBulkUpdateMode) {
      this.bulkUpdateBuyerMode(mode, buyerCode);
      return;
    }
    if (buyerId) {
      this.buyerCreate.isUpdating = true;
      this.buyerService.update({ buyerId, buyerCode } as BuyerRequest).subscribe(
        result => {
          this.buyerCreate.isUpdating = false;
          this.applyBuyerMode(mode, buyerCode);
          this.commonService.info('Updated buyer success.');
          this.editDone.emit(result);
        },
        error => {
          this.buyerCreate.isUpdating = false;
          this.commonService.warningHtml(error.message);
        },
      );
      return;
    }
    this.applyBuyerMode(mode, buyerCode);
  }

  private bulkUpdateBuyerMode(mode: 'buyer' | 'tenant', buyerCode: 'B' | 'T') {
    if (!this.bulkBuyerIds || this.bulkBuyerIds.length <= 1) {
      this.commonService.warning('Please select more than one buyer.');
      return;
    }
    this.buyerCreate.isUpdating = true;
    this.buyerService.bulkUpdate({
      buyerIds: this.bulkBuyerIds,
      request: { buyerCode } as BuyerRequest,
    }).subscribe(
      result => {
        this.buyerCreate.isUpdating = false;
        this.applyBuyerMode(mode, buyerCode);
        this.commonService.info('Updated selected buyers success.');
        this.editDone.emit(result);
      },
      error => {
        this.buyerCreate.isUpdating = false;
        this.commonService.warningHtml(error.message);
      },
    );
  }

  toggleWot() {
    const control = this.buyerCreate?.buyerForm?.get('isWot');
    if (!control) {
      return;
    }
    control.setValue(control.value !== true);
  }

  noAnswerAndNext() {
    const callControl = this.buyerCreate?.buyerForm?.get('call');
    if (callControl) {
      callControl.setValue(false);
    }
    if (this.buyerCreate?.data?.buyerId) {
      this.buyerCreate.logContactStatus(this.buyerCreate.contactLogType.NO_ANSWER);
      return;
    }
    this.navigateBuyer(true);
  }

  activeStatusLabel(): 'ACTIVE' | 'INACTIVE' {
    return this.buyerCreate?.buyerForm?.get('isActive')?.value === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE';
  }

  toggleActiveStatus() {
    const control = this.buyerCreate?.buyerForm?.get('isActive');
    if (!control) {
      return;
    }
    control.setValue(this.activeStatusLabel() === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE');
  }

  displayName(): string {
    const form = this.buyerCreate?.buyerForm;
    if (!form || this.isBlank) {
      return '';
    }
    return [form.controls?.name?.value, form.controls?.surname?.value]
      .filter(Boolean)
      .join(' ') || 'New Tenant';
  }

  dateCountText(): string {
    if (this.isBlank) {
      return '';
    }
    const enquiryDate = this.buyerCreate?.buyerForm?.get('enqDate2')?.value;
    if (!enquiryDate) {
      return '';
    }
    const fromDate = new Date(enquiryDate);
    if (Number.isNaN(fromDate.getTime())) {
      return '';
    }
    const today = new Date();
    fromDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    const days = Math.max(0, Math.floor((today.getTime() - fromDate.getTime()) / 86400000));
    return `${days}`;
  }

  sortedAgentNotes(): BuyerAgentNote[] {
    return [...(this.buyerCreate?.data?.agentNotes || [])]
      .sort((a, b) => Number(b.createdDate || 0) - Number(a.createdDate || 0));
  }

  formatAgentNoteDate(createdDate: number): string {
    return createdDate ? this.commonService.formatDate(createdDate, 'DD/MM/YYYY HH:mm') : '';
  }

  saveAgentNote() {
    const buyerId = this.buyerCreate?.data?.buyerId;
    const note = this.newAgentNote?.trim();
    if (!buyerId || !note || this.isSavingAgentNote) {
      return;
    }
    this.isSavingAgentNote = true;
    this.buyerService.createAgentNote(buyerId, { note }).subscribe(
      result => {
        this.isSavingAgentNote = false;
        this.newAgentNote = '';
        if (!this.buyerCreate.data.agentNotes) {
          this.buyerCreate.data.agentNotes = [];
        }
        this.buyerCreate.data.agentNotes.unshift(result.data);
        this.commonService.info('Created agent note success.');
      },
      error => {
        this.isSavingAgentNote = false;
        this.commonService.warningHtml(error.message);
      },
    );
  }

  depositLabelText(): string {
    return this.proofAmountLabel('Deposit', 2);
  }

  bondLabelText(): string {
    return this.proofAmountLabel('Bond', 4);
  }

  onCurrentRentalBrandChange(brand: string): void {
    this.currentRentalBrand = brand || null;
    this.currentRentalCompanies = CompanyUtils.filterByBrand(this.companyOptions, this.currentRentalBrand);
    this.buyerCreate?.buyerForm?.get('tenantCurrentCompanyId')?.setValue(null);
  }

  onPreviousRentalBrandChange(brand: string): void {
    this.previousRentalBrand = brand || null;
    this.previousRentalCompanies = CompanyUtils.filterByBrand(this.companyOptions, this.previousRentalBrand);
    this.buyerCreate?.buyerForm?.get('tenantPreviousCompanyId')?.setValue(null);
  }

  private forceTenantMode() {
    const form = this.buyerCreate?.buyerForm;
    if (!form) {
      return;
    }
    form.get('buyerCode')?.setValue('T');
    form.get('buyer')?.setValue(false);
    form.get('tenant')?.setValue(true);
    form.get('isFirstHome')?.setValue(false);
    form.get('isInvestor')?.setValue(false);
  }

  lookupRentalAddress(period: 'Current' | 'Previous'): void {
    const rentalKey = period === 'Current' ? 'currentRental' : 'previousRental';
    const address = this.buyerCreate?.data?.tenantApplicationApprovalDetail?.[rentalKey]?.address as Address;
    if (!this.hasRentalLookupSignal(address)) {
      this.commonService.warning(`${period} rental address does not have structured lookup data.`);
      return;
    }

    const request = this.buildRentalAddressLookupRequest(address);
    this.dialogService.open(BuyerLookupComponent, {
      context: {
        residenceLookupRequest: request,
        lookupOnly: true,
        lookupTitle: `${period} rental address lookup`,
      },
    });
  }

  private buildRentalAddressLookupRequest(address: Address): ProspectLivingAreaLookupRequest {
    const request = new ProspectLivingAreaLookupRequest();
    request.postcode = address.postCode || undefined;
    request.districtId = address.districtId || undefined;
    request.streetId = address.streetId || undefined;
    request.unit = this.normalizeLookupText(address.unit);
    request.numberFrom = this.normalizeLookupText(address.numberFrom || address.number);
    return request;
  }

  private hasRentalLookupSignal(address: Address): boolean {
    return !!address
      && !!address.postCode
      && !!address.districtId
      && !!address.streetId
      && !!this.normalizeLookupText(address.numberFrom || address.number);
  }

  private normalizeLookupText(value: string): string | undefined {
    const normalized = value == null ? '' : String(value).trim();
    return normalized || undefined;
  }

  private applyBuyerMode(mode: 'buyer' | 'tenant', buyerCode: 'B' | 'T') {
    const form = this.buyerCreate?.buyerForm;
    if (!form) {
      return;
    }
    const isSwitchingBuyerMode = form.get('buyerCode').value !== buyerCode;
    form.get('buyer')?.setValue(mode === 'buyer', { emitEvent: false });
    form.get('tenant')?.setValue(mode === 'tenant', { emitEvent: false });
    form.get('buyerCode')?.setValue(buyerCode);
    if (isSwitchingBuyerMode) {
      form.get('preferredAreas')?.setValue([]);
      form.get('preferredAreas')?.markAsPristine();
    }
    form.get('buyer')?.markAsPristine();
    form.get('tenant')?.markAsPristine();
    form.get('isFirstHome')?.markAsPristine();
    form.get('buyerCode')?.markAsDirty();
    if (this.buyerCreate?.data) {
      this.buyerCreate.data.buyerCode = buyerCode;
      if (isSwitchingBuyerMode) {
        this.buyerCreate.data.preferredAreas = [];
      }
    }
  }

  private proofAmountLabel(label: string, weeks: number): string {
    const amount = this.calculateRentWeeksAmount(weeks);
    const baseLabel = `${label} (${weeks} weeks rent price)`;
    return amount === null ? baseLabel : `${label} (${weeks} weeks rent price - $${this.formatAmount(amount)})`;
  }

  private calculateRentWeeksAmount(weeks: number): number | null {
    const rentPerWeek = this.currentRentPerWeek();
    return rentPerWeek === null ? null : rentPerWeek * weeks;
  }

  private currentRentPerWeek(): number | null {
    const value = this.buyerCreate?.buyerForm?.get('minBuyPrice')?.value;
    const amount = Number(value);
    return Number.isFinite(amount) && amount > 0 ? amount : null;
  }

  private formatAmount(amount: number): string {
    return amount.toLocaleString('en-AU', {
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      maximumFractionDigits: 2,
    });
  }

  private setRentalCompanyNames(buyer: Buyer) {
    const currentRental = buyer?.tenantApplicationApprovalDetail?.currentRental;
    const previousRental = buyer?.tenantApplicationApprovalDetail?.previousRental;
    const currentCompany = this.findCompany(currentRental?.companyId, currentRental?.company);
    const previousCompany = this.findCompany(previousRental?.companyId, previousRental?.company);
    this.currentRentalBrand = CompanyUtils.brand(currentCompany) || null;
    this.previousRentalBrand = CompanyUtils.brand(previousCompany) || null;
    this.currentRentalCompanies = this.includeSelectedCompany(
      CompanyUtils.filterByBrand(this.companyOptions, this.currentRentalBrand),
      currentCompany,
    );
    this.previousRentalCompanies = this.includeSelectedCompany(
      CompanyUtils.filterByBrand(this.companyOptions, this.previousRentalBrand),
      previousCompany,
    );
  }

  private findCompany(companyId: number, fallback?: Company): Company {
    return this.companyOptions.find(company => Number(company.companyId) === Number(companyId)) || fallback;
  }

  private includeSelectedCompany(companies: Company[], selected?: Company): Company[] {
    if (!selected || companies.some(company => Number(company.companyId) === Number(selected.companyId))) {
      return companies;
    }
    return [...companies, selected];
  }
}
